import { Button, Spinner } from "@mixitone/components";
import { Account } from "@mixitone/models";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import { isPresent, mixitoneImagesPublicUrl, omit } from "@mixitone/util";
import { AppController } from "controllers/AppController";
import { clubDocumentUploadError } from "lib/documentFormSave";
import { clubDocumentReadUrl, uploadClubDocumentObject } from "lib/documentUpload";
import { uploadMixitoneImageObject } from "lib/mixitoneImagesUpload";
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

interface PdfUploadProps {
  /**
   * Current PDF file ID value
   */
  value?: string;

  /**
   * Callback when PDF changes
   */
  onChange?: (value: string) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
  onLoad?: () => void;

  /**
   * Whether the upload is disabled
   */
  disabled?: boolean;

  /**
   * Additional CSS class names
   */
  className?: string;

  pageHeight?: number;

  /**
   * Club that owns a new private upload. When set, the file goes to the private documents bucket.
   */
  clubId?: string;

  /**
   * `private_documents` opens a signed URL. Older rows stay on the public images host.
   */
  fileStorage?: string | null;

  /**
   * Saved club document row. Private files that already have a row are signed from this id.
   */
  clubDocumentId?: string | null;

  ref?: React.MutableRefObject<HTMLElement | undefined> | React.RefCallback<HTMLDivElement | undefined>;
}

// biome-ignore lint/complexity/noBannedTypes: MVC scope with no persisted controller state
type State = {};

class PdfUploadController extends ApplicationController<State> {
  get initialState(): State {
    return {};
  }
}

/**
 * Component for uploading and displaying PDFs
 */
const ControlledPdfUpload = ({
  value,
  onChange,
  onUploadStart,
  onUploadEnd,
  onLoad,
  disabled = false,
  pageHeight,
  className = "",
  clubId,
  fileStorage,
  clubDocumentId,
  ref,
}: PdfUploadProps) => {
  const _pdfUpload = PdfUploadController.use();
  const appController = AppController.use();
  const offline = appController.state.online === false;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pdfUrl, setPdfUrl] = useState<string | undefined>();
  const [urlError, setUrlError] = useState<string | null>(null);
  const editable = isPresent(onChange);
  const isPrivate = fileStorage === "private_documents";

  useEffect(() => {
    if (!value) {
      setPdfUrl(undefined);
      setUrlError(null);
      return;
    }

    const accountId = Account.current?.id;
    if (!isPrivate) {
      setUrlError(null);
      setPdfUrl(accountId != null ? mixitoneImagesPublicUrl(`${accountId}/documents/${value}`) : undefined);
      return;
    }

    if (offline) {
      setPdfUrl(undefined);
      setUrlError("Connect to the Internet to view this document.");
      return;
    }

    let cancelled = false;
    setUrlError(null);
    clubDocumentReadUrl({
      fileStorage,
      accountId: accountId ?? "",
      fileId: value,
      clubDocumentId,
      clubId,
    })
      .then((url) => {
        if (!cancelled) setPdfUrl(url);
      })
      .catch((error) => {
        console.error("Error opening document:", error);
        if (!cancelled) {
          setPdfUrl(undefined);
          setUrlError("Could not open this document.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [value, isPrivate, fileStorage, clubDocumentId, clubId, offline]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    if (onLoad) onLoad();
  }

  const upload = useCallback(
    async (file: File) => {
      if (!onChange) return;
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }

      setUploading(true);
      setUploadError(null);
      onUploadStart?.();

      try {
        const accountId = Account.current?.id;
        if (!accountId) throw new Error("Not signed in");

        if (clubId) {
          const fileId = await uploadClubDocumentObject({ accountId, clubId, file });
          onChange(fileId);
        } else {
          const uid = crypto.randomUUID();
          await uploadMixitoneImageObject({
            accountId,
            relativePath: `documents/${uid}`,
            file,
          });
          onChange(uid);
        }
      } catch (error) {
        console.error("Error uploading PDF:", error);
        setUploadError(clubDocumentUploadError(error));
      } finally {
        setUploading(false);
        onUploadEnd?.();
      }
    },
    [onChange, clubId],
  );

  const handleFileSelect: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) upload(file);
    };
    input.click();
  };

  const handleClickPrevious = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };
  const handleClickNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (numPages && pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  if (value) {
    if (urlError) {
      return (
        <div className={`flex h-24 w-full items-center justify-center rounded-lg bg-gray-100 ${className}`}>
          {urlError}
        </div>
      );
    }

    if (!pdfUrl) {
      return <Spinner size={32} />;
    }

    return (
      <div
        className={`relative w-full group ${className}`}
        ref={(el) => {
          if (typeof ref === "function") {
            ref(el);
          } else if (ref && "current" in ref) {
            ref.current = el ?? undefined;
          }
        }}
      >
        {uploadError && <p className="mb-2 text-center text-sm text-red-600">{uploadError}</p>}
        {editable && (
          <Button
            kind="secondary"
            className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleFileSelect}
            disabled={offline || disabled || uploading}
          >
            {uploading ? "Uploading..." : "Replace"}
          </Button>
        )}
        <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess} loading={<Spinner size={64} />}>
          <Page
            pageNumber={pageNumber}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            height={pageHeight ?? undefined}
          />
        </Document>
        {numPages && numPages > 1 && (
          <div className="flex items-center justify-center mt-4 space-x-4">
            <Button kind="secondary" onClick={handleClickPrevious} disabled={pageNumber <= 1}>
              Previous
            </Button>
            <span>
              Page {pageNumber} of {numPages}
            </span>
            <Button kind="secondary" onClick={handleClickNext} disabled={pageNumber >= numPages}>
              Next
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (editable) {
    return (
      <div
        className={`flex min-h-24 w-full flex-col items-center justify-center gap-2 rounded-lg bg-gray-100 px-3 py-3 ${className}`}
      >
        {uploadError && <p className="text-center text-sm text-red-600">{uploadError}</p>}
        <Button kind="secondary" onClick={handleFileSelect} disabled={disabled || uploading}>
          {uploading ? "Uploading..." : "Upload document"}
        </Button>
      </div>
    );
  } else {
    return (
      <div className={`flex h-24 w-full items-center justify-center rounded-lg bg-gray-100 ${className}`}>
        No document found
      </div>
    );
  }
};

export function useSizePdfInModal() {
  const modalRef = useRef<HTMLElement | undefined>(undefined);
  const pdfRef = useRef<HTMLElement | undefined>(undefined);
  const [pageHeight, setPageHeight] = useState<number | undefined>(undefined);
  const [pdfLoaded, setPdfLoaded] = useState(false);

  useEffect(() => {
    if (!pdfLoaded) return;
    if (pageHeight !== undefined) return;
    if (!modalRef.current || !pdfRef.current) return;

    const modalBounds = modalRef.current.getBoundingClientRect();
    const pdfBounds = pdfRef.current.getBoundingClientRect();

    const availableHeight = modalBounds.height - (modalBounds.top + pdfBounds.top + 20);
    setPageHeight(availableHeight);
  }, [pdfLoaded]);

  const handleLoad = useCallback(() => {
    setPageHeight(undefined);
    setTimeout(() => {
      setPdfLoaded(true);
    }, 10);
  }, []);

  return { pageHeight, pdfLoaded, handleLoad, pdfRef, modalRef };
}

export const PdfUpload = PdfUploadController.scope(ApplicationView(ControlledPdfUpload));

export const PdfViewer = (props: { documentId: string } & Omit<PdfUploadProps, "value" | "onChange">) => {
  return <PdfUpload value={props.documentId} {...omit(props, ["documentId"])} />;
};
